import React, { useEffect, lazy, Suspense } from 'react';
import { useQuery } from 'urql';
import { useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
	healthyDealsAFDDetailsQuery,
	healthyDealsVendorDetailsQuery,
	healthyDealsSpecialtyDetailsQuery,
} from '../../graphql/queries';
import DealsGrid from '../ListOfDeals/DealsGrid';
import BackLink from '../BackLink';
import { useHealthyDealsDataContext } from '../HealthyDealsDataContextProvider/HealthyDealsData.context';
import DealDetailsPageSkeleton from '../Skeletons/DealDetailsPageSkeleton';
import {
	afdPageMetaDescription,
	specialtyPageMetaDescription,
	vendorPageMetaDescription,
} from '../../utils/miscConstants';

const VendorDeal = lazy(() => import('./VendorDeal'));
const AFDDeal = lazy(() => import('./AFDDeal'));
const SpecialtyDeal = lazy(() => import('./SpecialtyDeal'));

const detailsQuery = {
	afd: healthyDealsAFDDetailsQuery,
	vendor: healthyDealsVendorDetailsQuery,
	specialty: healthyDealsSpecialtyDetailsQuery,
};

const returnDetailsComponentBasedOnType = (dealType, dealDetails, fetching) => {
	switch (dealType) {
		case 'vendor':
			return <VendorDeal {...dealDetails} loading={fetching} />;
		case 'afd':
			return <AFDDeal {...dealDetails} loading={fetching} />;
		case 'specialty':
			return <SpecialtyDeal {...dealDetails} loading={fetching} />;
		default:
			return null;
	}
};

export default function Deal() {
	const { dealId, dealType } = useParams();

	const { setPrevPageTitle, updatePageTitleAndDescription } =
		useHealthyDealsDataContext();

	const [result] = useQuery({
		query: detailsQuery[dealType],
		variables: {
			dealId,
		},
	});

	const { fetching, error, data } = result;
	if (error) {
		throw error; // should bubble up to the consumer
	}

	const dealDetails = data?.healthyDealsDealDetail;

	useEffect(() => {
		if (!fetching) {
			if (dealType === 'specialty') {
				updatePageTitleAndDescription(
					dealDetails?.detailsTitle,
					specialtyPageMetaDescription(dealDetails?.detailsTitle),
				);
				setPrevPageTitle(dealDetails?.detailsTitle);
			} else {
				const pageMetaDescription =
					dealType === 'afd'
						? afdPageMetaDescription
						: vendorPageMetaDescription(dealDetails?.cardTitle);

				updatePageTitleAndDescription(
					dealDetails?.cardTitle,
					pageMetaDescription,
				);
				setPrevPageTitle(dealDetails?.cardTitle);
			}
		}
	}, [dealDetails?.detailsTitle, dealDetails?.cardTitle, dealType]);

	return (
		<>
			<Grid mb={3}>
				<BackLink />
			</Grid>
			<Suspense fallback={<DealDetailsPageSkeleton dealType={dealType} />}>
				<Grid container component="article">
					{returnDetailsComponentBasedOnType(dealType, dealDetails, fetching)}
				</Grid>
				<Grid container mt={3}>
					{!fetching && dealDetails?.recommendedDeals && (
						<>
							<Typography variant="h5" component="h2" gutterBottom>
								Recommended Deals
							</Typography>
							<DealsGrid
								deals={dealDetails?.recommendedDeals}
								loading={fetching}
								isRecommendedGrid
								recommendedForDealName={
									dealDetails?.dealType === 'specialty'
										? dealDetails?.detailsTitle
										: dealDetails?.dealName
								}
							/>
						</>
					)}
				</Grid>
			</Suspense>
		</>
	);
}
